import { inject, Injectable } from '@angular/core';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AppPathsMap } from '@core/routing/app-paths';
import { LoggerService } from '@kerberos-compliance/compliance-shared-ui';
import { UserProfileFacade } from '@/user/facades/user-profile.facade';

@Injectable({
  providedIn: 'root',
})
export class DeepLinkService {
  private navigationService = inject(NavigationService);

  private userProfileFacade = inject(UserProfileFacade);

  private readonly logger = inject(LoggerService);

  /**
   * Resolves where the user should land after authenticating, from the last
   * visited URL persisted on their profile. The stored preference is consumed
   * (cleared) so it is only ever honoured once.
   *
   * @returns {string | undefined} - The resolved URL to redirect to, or `undefined` when there is no valid target.
   */
  public handlePostAuthRedirect(): string | undefined {
    const lastVisited = this.userProfileFacade.userProfile()?.userPreferences?.lastVisitedUrl;
    if (lastVisited) {
      void this.clearLastVisitedUrl();
      return this.getValidRedirectUrl(lastVisited);
    }

    return undefined;
  }

  /**
   * Same as {@link handlePostAuthRedirect}, but waits for the stored preference to
   * be cleared before resolving. Required by callers that trigger a FULL-PAGE load
   * with the result (the Hanko login completion): the unload would abort the
   * in-flight clearing request, and the stale URL would then be restored on every
   * subsequent login.
   *
   * @returns {Promise<string | undefined>} The resolved URL to redirect to, or `undefined` when there is no valid target.
   */
  public async consumePostAuthRedirect(): Promise<string | undefined> {
    const lastVisited = this.userProfileFacade.userProfile()?.userPreferences?.lastVisitedUrl;
    if (!lastVisited) {
      return undefined;
    }
    await this.clearLastVisitedUrl();
    return this.getValidRedirectUrl(lastVisited);
  }

  /**
   * Navigates to the intended URL following an authentication process, falling
   * back to the home route when there is no valid stored target.
   *
   * @returns {Promise<boolean>} A promise that resolves to a boolean indicating whether navigation was triggered.
   */
  public async navigateToIntendedUrl(): Promise<boolean> {
    const targetUrl = this.handlePostAuthRedirect();
    const defaultRoute = `/${AppPathsMap.Home}`;

    void this.navigationService.navigateByUrl(targetUrl ?? defaultRoute);
    return true;
  }

  /**
   * Consumes the stored last-visited URL. Best-effort: failing to clear it must
   * never block the login it is part of, so a rejected write is logged, not
   * propagated (the worst case is the destination being offered once more).
   *
   * @returns {Promise<void>} Resolves once the write has been attempted.
   */
  private async clearLastVisitedUrl(): Promise<void> {
    try {
      await this.userProfileFacade.updateUserPreferences({ lastVisitedUrl: undefined });
    } catch (error) {
      this.logger.warn('Could not clear the last visited URL', 'DeepLinkService', error);
    }
  }

  /**
   * Validates and returns a sanitized redirect URL if it is allowed.
   *
   * @param {string} url - The original URL to be validated and sanitized.
   * @returns {string | undefined} The sanitized URL if valid and not excluded, or undefined if invalid or excluded.
   */
  private getValidRedirectUrl(url: string): string | undefined {
    const sanitized = this.sanitizeUrl(url);
    return sanitized && !this.isExcludedRoute(sanitized) ? sanitized : undefined;
  }

  /**
   * Checks if a URL matches any excluded route pattern. Landing back on the login
   * page or an error page right after a successful login would be a dead end.
   *
   * @param {string} url - The URL to check against excluded routes.
   * @returns {boolean} True if the URL is excluded, false otherwise.
   */
  private isExcludedRoute(url: string): boolean {
    const excludedPaths = [
      AppPathsMap.Homepage,
      `/${AppPathsMap.Login}`,
      `/${AppPathsMap.Logout}`,
      `/${AppPathsMap.Error.Unauthorized}`,
      `/${AppPathsMap.Error.UserNotActive}`,
      `/${AppPathsMap.Error.NoGrants}`,
      `/${AppPathsMap.Error.GrantLoadError}`,
      `/${AppPathsMap.Error.Maintenance}`,
      `/${AppPathsMap.Error.NotFound}`,
    ];
    return excludedPaths.includes(url) || url.startsWith(`/${AppPathsMap.Error.BackendUnavailable}`);
  }

  /**
   * Sanitizes a given URL by removing the protocol and domain, leaving only the
   * path component, so a stored value can never redirect off-origin.
   *
   * @param {string} url - The URL to sanitize.
   * @returns {string | undefined} Returns the sanitized path if the input URL is valid; otherwise, returns undefined.
   */
  private sanitizeUrl(url: string): string | undefined {
    if (!url) {
      return undefined;
    }
    // Handle both http(s):// and protocol-relative // URLs
    // eslint-disable-next-line security/detect-unsafe-regex
    const pathOnly = url.replace(/^(?:https?:)?\/\/[^/]+/, '');
    if (!pathOnly) {
      return '/';
    }
    return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  }
}
